import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { AutoComplete } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router-dom';
import StockService from '../../services/StockService';
import ProductService from '../../services/ProductService';
import WarehouseService from '../../services/WarehouseService';

const movementTypeLabels = {
    1: { label: 'AÇILIŞ STOKU', severity: 'info', icon: 'pi pi-plus-circle' },
    2: { label: 'ALIŞ', severity: 'success', icon: 'pi pi-shopping-cart' },
    3: { label: 'SATIŞ', severity: 'danger', icon: 'pi pi-shopping-bag' },
    4: { label: 'MÜŞTERİ İADESİ', severity: 'success', icon: 'pi pi-refresh' },
    5: { label: 'TEDARİKÇİ İADESİ', severity: 'danger', icon: 'pi pi-refresh' },
    6: { label: 'MANUEL GİRİŞ', severity: 'info', icon: 'pi pi-arrow-down-left' },
    7: { label: 'MANUEL ÇIKIŞ', severity: 'warning', icon: 'pi pi-arrow-up-right' },
    8: { label: 'SAYIM DÜZELTME', severity: 'warning', icon: 'pi pi-pencil' },
    9: { label: 'FİRE / ZAYİ', severity: 'danger', icon: 'pi pi-trash' },
    10: { label: 'TRANSFER (ÇIKIŞ)', severity: 'warning', icon: 'pi pi-send' },
    11: { label: 'TRANSFER (GİRİŞ)', severity: 'info', icon: 'pi pi-download' }
};

export default function StockMovementListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        warehouseId: null
    });
    const toast = useRef(null);

    // Ürün Özet Popup State'leri
    const [detailDialogVisible, setDetailDialogVisible] = useState(false);
    const [detailProduct, setDetailProduct] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const showProductSummary = async (productId) => {
        if (!productId) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Bu harekete ait geçerli bir ürün kimliği bulunamadı.' });
            return;
        }
        setDetailProduct(null);
        setDetailDialogVisible(true);
        setDetailLoading(true);
        try {
            const res = await ProductService.getById(productId);
            const productData = res.data?.data || res.data;
            if (productData) {
                setDetailProduct(productData);
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ürün detayları yüklenemedi.' });
            }
        } catch (err) {
            console.error('Ürün detay yükleme hatası:', err);
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ürün bilgileri getirilirken bir hata oluştu.' });
        } finally {
            setDetailLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                startDate: filters.startDate?.toISOString(),
                endDate: filters.endDate?.toISOString(),
                productId: selectedProduct?.id,
                warehouseId: filters.warehouseId
            };
            const res = await StockService.getStockMovements(params);
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenemedi' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        loadLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLookups = async () => {
        try {
            const warRes = await WarehouseService.getAll();
            const warData = warRes.data?.data || warRes.data;
            if (warData) {
                setWarehouses(warData.map(w => ({ label: w.name, value: w.id })));
            }
        } catch (err) {
            console.error('Lookup load error', err);
        }
    };

    const searchProducts = async (event) => {
        if (event.query.trim().length < 3) {
            setFilteredProducts([]);
            return;
        }

        try {
            const res = await ProductService.getAutoComplete(event.query);
            const productData = res.data?.data || res.data;
            if (productData && Array.isArray(productData)) {
                setFilteredProducts(productData.map(p => ({
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    label: `${p.name} (${p.barcode || 'Barkodsuz'})`
                })));
            }
        } catch (error) {
            console.error('Ürün arama hatası:', error);
        }
    };

    const movementDetailTemplate = (rowData) => {
        const isIn = rowData.direction === 1;
        const typeInfo = movementTypeLabels[rowData.movementType] || { label: 'BİLİNMEYEN', severity: 'info', icon: 'pi pi-info-circle' };
        return (
            <div className="flex flex-col md:flex-row md:items-center gap-2">
                {/* Movement Type with Icon */}
                <div className="flex items-center gap-2">
                    <i className={`${typeInfo.icon} text-slate-400 dark:text-slate-500 text-sm`} />
                    <span className="text-[11px] font-black text-slate-700 dark:text-slate-300 uppercase tracking-tight">{typeInfo.label}</span>
                </div>
                {/* Direction Badge */}
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg w-fit ${isIn ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' : 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400'}`}>
                    <i className={`pi ${isIn ? 'pi-arrow-down-left' : 'pi-arrow-up-right'} text-[9px] font-bold`}></i>
                    <span className="text-[9px] font-black uppercase tracking-wider">{isIn ? 'GİRİŞ' : 'ÇIKIŞ'}</span>
                </div>
            </div>
        );
    };

    const dateTemplate = (rowData) => {
        return (
            <div className="flex flex-col">
                <span className="font-black text-slate-700 dark:text-slate-300 text-sm">{new Date(rowData.movementDate).toLocaleDateString('tr-TR')}</span>
                <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tighter">{new Date(rowData.movementDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
        );
    };

    const quantityTemplate = (rowData) => {
        const isIn = rowData.direction === 1;
        return (
            <div className="flex flex-col items-end">
                <span className={`font-black text-base tracking-tighter ${isIn ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {isIn ? '+' : '-'}{rowData.quantity.toLocaleString('tr-TR')}
                </span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold">Miktar</span>
            </div>
        );
    };

    const productTemplate = (rowData) => {
        return (
            <div className="flex flex-col">
                <span
                    onClick={() => rowData.productId && navigate(`/products/detail/${rowData.productId}`)}
                    className="font-black text-slate-800 dark:text-slate-100 text-sm leading-tight cursor-pointer hover:text-indigo-600 hover:underline transition-colors"
                >
                    {rowData.productName}
                </span>
                <div className="flex items-center gap-1.5 mt-1">
                    <i className="pi pi-home text-[10px] text-slate-700 dark:text-slate-500" />
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase">{rowData.warehouseName}</span>
                </div>
            </div>
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <div className="flex justify-center">
                <Button
                    icon="pi pi-eye"
                    onClick={() => showProductSummary(rowData.productId)}
                    className="p-button-text p-button-rounded p-button-plain hover:!bg-slate-100 dark:hover:!bg-slate-800 !text-indigo-600 dark:text-indigo-400 !w-8 !h-8 !p-0 flex items-center justify-center"
                    tooltip="Hızlı Özet"
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    // Calculate Today's Stats
    const today = new Date().toDateString();
    const todayIn = data.filter(d => d.direction === 1 && new Date(d.movementDate).toDateString() === today).reduce((acc, d) => acc + d.quantity, 0);
    const todayOut = data.filter(d => d.direction === 2 && new Date(d.movementDate).toDateString() === today).reduce((acc, d) => acc + d.quantity, 0);

    const header = (
        <div className="bg-slate-200 dark:bg-slate-800/50 p-4 rounded-t-[2rem] border-b border-slate-100 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-6 pb-4">
                <label className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Ürün Filtrele</label>
                <AutoComplete
                    value={selectedProduct}
                    suggestions={filteredProducts}
                    completeMethod={searchProducts}
                    field="name"
                    placeholder="Ürün Adı (Min 3 Harf)"
                    className="w-full md:w-56"
                    inputClassName="w-full custom-input py-3"
                    onChange={(e) => setSelectedProduct(e.value)}
                    showClear
                />
            </div>
            <div className="flex flex-col xl:flex-row justify-between gap-2">

                <div className="flex flex-wrap gap-6 flex-1">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <Calendar value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.value })} placeholder="Başlangıç" showIcon className="w-full md:w-40 custom-calendar" />
                        <Calendar value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.value })} placeholder="Bitiş" showIcon className="w-full md:w-40 custom-calendar" />

                        <Dropdown value={filters.warehouseId} options={warehouses} onChange={(e) => setFilters({ ...filters, warehouseId: e.value })} placeholder="Depo" showClear className="w-full md:w-48 rounded-xl" />
                        <Button icon="pi pi-search" onClick={fetchData} className="!bg-indigo-600 border-none px-6 rounded-xl font-black text-xs" label="FİLTRELE" />

                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="p-input-icon-left w-full md:w-64">
                        <i className="pi pi-search text-slate-400 pl-4" />
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Hızlı ara..." className="w-full p-4 ml-3  !py-2 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm dark:text-white" />
                    </span>
                    <Button icon="pi pi-refresh" onClick={() => { setFilters({ startDate: null, endDate: null, warehouseId: null }); setSelectedProduct(null); fetchData(); }} className="p-button-outlined ml-3 p-button-secondary rounded-xl h-[38px] w-[38px] dark:border-slate-700 dark:text-slate-400" />
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-2 md:p-6 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[3rem] animate-fade-in max-w-[1600px] mx-auto">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            {/* Ürün Hızlı Özet Dialog */}
            <Dialog
                header={detailProduct ? `${detailProduct.name} - Hızlı Özet` : "Yükleniyor..."}
                visible={detailDialogVisible}
                onHide={() => setDetailDialogVisible(false)}
                style={{ width: '450px' }}
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                modal
                draggable={false}
                resizable={false}
                className="custom-detail-dialog"
                contentClassName="!p-0"
                headerClassName="!bg-slate-900 !text-white !p-6 !rounded-t-3xl"
            >
                {detailLoading ? (
                    <div className="flex flex-col items-center justify-center p-12 space-y-4">
                        <i className="pi pi-spin pi-spinner text-4xl text-indigo-600"></i>
                        <span className="text-sm font-bold text-slate-500">Ürün bilgileri yükleniyor...</span>
                    </div>
                ) : detailProduct ? (
                    <div className="p-2 space-y-3 bg-slate-50 dark:bg-slate-950">
                        {/* Bilgi Kartları */}
                        <div className="space-y-4">
                            {/* Son Alış Fiyatı */}
                            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex items-center gap-4">
                                <div className="w-2 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center text-amber-600 flex-shrink-0">
                                    <i className="pi pi-shopping-cart text-s" />
                                </div>
                                <div className="flex-1">
                                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block">Son Alış Fiyatı</span>
                                    <span className="text-lg font-black text-slate-800 dark:text-slate-200">
                                        {detailProduct.lastPurchase?.unitPrice
                                            ? `${detailProduct.lastPurchase.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`
                                            : 'Kayıt yok'}
                                    </span>
                                    {detailProduct.lastPurchase?.supplierName && (
                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 block font-semibold mt-0.5">
                                            {detailProduct.lastPurchase.supplierName}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Satış Fiyatı */}
                            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex items-center gap-4">
                                <div className="w-2 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                                    <i className="pi pi-tags text-s" />
                                </div>
                                <div className="flex-1">
                                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block">KDV Dahil Satış Fiyatı</span>
                                    <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                                        {detailProduct.salePriceInclVat?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                    </span>
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 block font-semibold mt-0.5">
                                        KDV Hariç: {detailProduct.salePriceExclVat?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺ (%{detailProduct.taxRate})
                                    </span>
                                </div>
                            </div>

                            {/* Depolardaki Stok Durumu */}
                            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-3">
                                <div className="flex items-center gap-3 border-b border-slate-50 dark:border-slate-800 pb-2">
                                    <div className="w-2 h-8 rounded-lg !bg-indigo-100 dark:bg-indigo-950/30 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                        <i className="pi pi-box text-s" />
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block">Depo Stok Dağılımı</span>
                                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                            Toplam: {detailProduct.currentStockQuantity?.toLocaleString('tr-TR')} {detailProduct.unitType || ''}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                                    {detailProduct.warehouseStocks && detailProduct.warehouseStocks.length > 0 ? (
                                        detailProduct.warehouseStocks.map((ws, i) => (
                                            <div key={ws.warehouseId || i} className="flex justify-between items-center text-xs font-bold py-1">
                                                <span className="text-slate-600 dark:text-slate-400">{ws.warehouseName}</span>
                                                <span className={`px-2.5 py-0.5 rounded-lg text-[11px] font-black ${ws.quantity > 0 ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-850 text-slate-500'}`}>
                                                    {ws.quantity?.toLocaleString('tr-TR')} {detailProduct.unitType || ''}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-xs text-slate-400 dark:text-slate-500 py-2">
                                            Hiçbir depoda stok kaydı bulunamadı.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Butonlar */}
                        <div className="flex justify-end gap-3 pt-2">
                            <Button
                                label="Kapat"
                                className="p-button-outlined p-button-secondary rounded-xl font-bold text-xs"
                                onClick={() => setDetailDialogVisible(false)}
                            />
                            <Button
                                label="Detay Sayfasına Git"
                                icon="pi pi-external-link"
                                className="!bg-indigo-600 hover:!bg-indigo-700 border-none rounded-xl font-bold text-xs px-4"
                                onClick={() => {
                                    setDetailDialogVisible(false);
                                    navigate(`/products/detail/${detailProduct.id}`);
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="p-6 text-center text-slate-500">
                        Bir hata oluştu veya ürün bilgisi bulunamadı.
                    </div>
                )}
            </Dialog>

            {/* Elite Page Header */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-10 gap-6 p-6 bg-slate-900 dark:bg-slate-950 rounded-[2.5rem] text-white border border-slate-800 shadow-2xl">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2.2rem] !bg-indigo-600 flex items-center justify-center shadow-2xl border-4 border-slate-800">
                        <i className="pi pi-history text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-white leading-none uppercase tracking-tighter">Stok Hareketleri</h1>
                        <p className="text-slate-400 font-bold text-sm uppercase tracking-widest mt-2 italic flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Global Envanter Denetim Günlüğü
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 w-full lg:w-auto">
                    <div className="flex-1 lg:flex-none bg-slate-800/50 dark:bg-slate-900/50 p-4 rounded-3xl border border-slate-700/50 dark:border-slate-800/80 flex items-center gap-4 px-6 text-white">
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest m-0 leading-tight">Bugünkü Giriş</p>
                            <h4 className="text-xl font-black text-emerald-400 m-0 leading-tight">+{todayIn.toLocaleString('tr-TR')}</h4>
                        </div>
                        <div className="w-px h-8 bg-slate-700" />
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest m-0 leading-tight">Bugünkü Çıkış</p>
                            <h4 className="text-xl font-black text-rose-400 m-0 leading-tight">-{todayOut.toLocaleString('tr-TR')}</h4>
                        </div>
                    </div>
                    <Button label="YENİ İŞLEM" icon="pi pi-plus" className="!bg-indigo-600 hover:!bg-indigo-700 border-none rounded-3xl px-6 h-[64px] font-black text-xs text-white shadow-xl transition-all hover:scale-[1.02]" onClick={() => navigate('/stock/adjustment')} />
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-slate-900 shadow-2xl shadow-slate-400 dark:shadow-none border border-slate-100 dark:border-slate-800 rounded-[3rem] overflow-hidden">
                <DataTable
                    value={data}
                    loading={loading}
                    header={header}
                    globalFilter={globalFilter}
                    paginator
                    showGridlines
                    rows={15}
                    rowsPerPageOptions={[15, 25, 50, 100]}
                    className="p-datatable-sm elite-table"
                    emptyMessage="Herhangi bir stok hareketi kaydı bulunamadı."
                    rowHover
                    removableSort
                    responsiveLayout="stack"
                    breakpoint="960px"
                    dataKey="id"
                >
                    <Column field="movementDate" header="ZAMAN ÇİZELGESİ" body={dateTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-2 tracking-widest" className="p-4" style={{ width: '150px' }} />
                    <Column field="productName" header="ÜRÜN & DEPO" body={productTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" />
                    <Column body={actionTemplate} header="ÖZET" headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest" className="p-2 text-center" style={{ width: '80px' }} />
                    <Column field="movementType" header="HAREKET DETAYI" body={movementDetailTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" />
                    <Column field="quantity" header="MİKTAR" body={quantityTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest  " className="p-4 text-right" style={{ width: '120px' }} />
                    <Column field="balanceAfterMovement" header="KALAN" body={(r) => (
                        <div className="flex flex-col items-end">
                            <span className="font-black text-slate-700 dark:text-slate-300 text-sm tracking-tighter">{r.balanceAfterMovement.toLocaleString('tr-TR')}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tighter">Bakiye</span>
                        </div>
                    )} sortable headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" style={{ width: '120px' }} />
                    <Column field="description" header="SİSTEM NOTU" body={(r) => <span className="text-slate-700 dark:text-slate-400 font-medium text-[12px] italic">{r.description || '—'}</span>} headerClassName="bg-transparent text-center text-slate-700 dark:text-slate-300 font-black text-[12px] uppercase p-4 tracking-widest" className="p-2" />
                </DataTable>
            </div>

            <style>{`
                .custom-detail-dialog .p-dialog-header {
                    background: #0f172a !important;
                    color: white !important;
                    padding: 1.5rem !important;
                    border-top-left-radius: 1.5rem !important;
                    border-top-right-radius: 1.5rem !important;
                }
                .custom-detail-dialog .p-dialog-content {
                    background: #f8fafc !important;
                    border-bottom-left-radius: 1.5rem !important;
                    border-bottom-right-radius: 1.5rem !important;
                    padding: 0 !important;
                }
                .dark .custom-detail-dialog .p-dialog-header {
                    background: #020617 !important;
                    border-color: #1e293b !important;
                }
                .dark .custom-detail-dialog .p-dialog-content {
                    background: #090d16 !important;
                }
                .elite-table .p-datatable-tbody > tr {
                    border-bottom: 1px solid #f1f5f9;
                    transition: all 0.2s;
                }
                .dark .elite-table .p-datatable-tbody > tr {
                    border-bottom: 1px solid #1e293b;
                }
                .elite-table .p-datatable-tbody > tr:hover {
                    background: #f8fafc !important;
                    transform: translateX(4px);
                }
                .dark .elite-table .p-datatable-tbody > tr:hover {
                    background: #1e293b/50 !important;
                }
                .custom-calendar .p-inputtext, .custom-input {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.75rem;
                    font-weight: 700;
                    font-size: 0.875rem;
                    padding: 0.5rem 1rem;
                }
                .dark .custom-calendar .p-inputtext, .dark .custom-input {
                    background: #1e293b;
                    border-color: #334155;
                    color: white;
                }
                .p-dropdown {
                    background: #f8fafc;
                    border-color: #e2e8f0;
                }
                .dark .p-dropdown {
                    background: #1e293b;
                    border-color: #334155;
                }
                .p-paginator {
                    background: transparent;
                    border: none;
                    padding: 2rem;
                }

                /* Dark Mode Datatable Styles */
                .dark .p-datatable {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                }
                .dark .p-datatable-header,
                .dark .p-datatable-thead > tr > th,
                .dark .p-datatable-tbody > tr {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                    border-color: #1e293b !important;
                }
                .dark .p-paginator {
                    background: #0f172a !important;
                    color: #cbd5e1 !important;
                    border-color: #1e293b !important;
                }
                .dark .p-paginator .p-paginator-first,
                .dark .p-paginator .p-paginator-prev,
                .dark .p-paginator .p-paginator-pages .p-paginator-page,
                .dark .p-paginator .p-paginator-next,
                .dark .p-paginator .p-paginator-last {
                    color: #cbd5e1 !important;
                }
                .dark .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
                    background: #1e293b !important;
                    color: #38bdf8 !important;
                }
                .dark .p-dropdown .p-dropdown-label {
                    color: white !important;
                }
                .dark .p-dropdown .p-dropdown-trigger {
                    color: #cbd5e1 !important;
                }
                .dark .p-dropdown-panel {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    color: #cbd5e1 !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover,
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #0f172a !important;
                    color: #38bdf8 !important;
                }
                .dark .p-calendar .p-datepicker {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                    color: white !important;
                }
                .dark .p-calendar .p-datepicker th, .dark .p-calendar .p-datepicker td span {
                    color: white !important;
                }
                .dark .p-calendar .p-datepicker td span.p-disabled {
                    color: #475569 !important;
                }
                .dark .p-autocomplete-panel {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                }
                .dark .p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item {
                    color: #cbd5e1 !important;
                }
                .dark .p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item:hover {
                    background: #0f172a !important;
                }
                .dark .p-datatable-loading-overlay {
                    background: rgba(15, 23, 42, 0.7) !important;
                }
            `}</style>
        </div>
    );
}
